/**
 * Tuning presets. Each preset defines:
 *   - scaleSize: number of distinct notes per octave
 *   - scaleNotes: intervals from the bass (semitones + microtonal cents)
 *   - defaultBass: sensible starting bass note for a 17-string instrument
 *
 * Sizes 1–12 are covered with the common musical choices for each size.
 */

interface PresetNote {
  semitones: number;
  cents: number;
}

export interface TuningPreset {
  key: string;
  label: string;
  scaleSize: number;
  scaleNotes: readonly PresetNote[];
  defaultBass: { pitchClass: string; octave: number };
  /** Short hint shown under the dropdown. */
  hint?: string;
  /** If set, the preset pins string[0] to this scale-degree index (0-based)
   *  AND uses defaultBass.octave verbatim — bypassing the auto-octave
   *  heuristic. Used when a preset's idiom depends on a specific bottom
   *  pitch (e.g. Sakura/In starts on the E degree, not the root). */
  defaultLowestDegreeIdx?: number;
  /** Cents per scale period. Defaults to 1200 (one octave) when unset.
   *  Use for non-octave scales: Bohlen-Pierce (1902, the tritave), Carlos
   *  Alpha (78, a single generator step), etc. */
  scalePeriod?: number;
  /** Authoritative reference URL — Wikipedia / Xenharmonic Wiki / scholarly
   *  source for the cents values. Rendered as a hostname link under the
   *  preset card so curious players can dig in. */
  sourceUrl?: string;
  /** Global detune of the whole instrument, in cents, applied to the bass
   *  (and thus every string). Pins the tuning to a singer's reference pitch
   *  (e.g. Hò = G#3 −21¢) without leaving equal temperament inside the scale.
   *  Defaults to 0. */
  defaultBassCents?: number;
}

const n = (s: number, c = 0): PresetNote => ({ semitones: s, cents: c });

/** Shorthand to build a preset. Trailing options are bag-form so we don't
 *  pile positional args as fields grow. */
const p = (
  key: string,
  label: string,
  scaleSize: number,
  scaleNotes: readonly PresetNote[],
  bassPitchClass: string,
  bassOctave: number,
  hint?: string,
  opts?: {
    defaultLowestDegreeIdx?: number;
    scalePeriod?: number;
    sourceUrl?: string;
    defaultBassCents?: number;
  },
): TuningPreset => ({
  key,
  label,
  scaleSize,
  scaleNotes,
  defaultBass: { pitchClass: bassPitchClass, octave: bassOctave },
  hint,
  defaultLowestDegreeIdx: opts?.defaultLowestDegreeIdx,
  scalePeriod: opts?.scalePeriod,
  sourceUrl: opts?.sourceUrl,
  defaultBassCents: opts?.defaultBassCents,
});

// ── ĐÀN TRANH theo bản thu (vọng cổ + hát ru Nam Bộ) — port từ DanTranhTab ──
const VC_PENTA: readonly PresetNote[] = [n(0), n(2), n(4), n(7), n(9)];
// Dây THẤP NHẤT cách Hò 2 bậc thang → lowestDegreeIdx = size − LOWEST_BELOW_ROOT.
const LOWEST_BELOW_ROOT = 2;
// Helper CHUNG cho MỌI hệ lên dây đàn tranh microtone (vọng cổ, hát ru Nam Bộ, …).
//   · Giữ NGUYÊN microtone trong scaleNotes (KHÔNG ép 12-EDO) — số đo thật từ [DTT].
//   · bassCents khử micro-cents của BẬC THẤP NHẤT → Hò rơi đúng tên-nốt + lệch `cents`.
//   · opts.lowestDegreeIdx ÉP dây thấp nhất vào một bậc (vd 'D E G A C' = 2 dây dưới
//     quãng-5 → trỏ bậc D). Mặc định size−2 (Hò là dây thứ ba, đỡ căng).
// THÊM HỆ MỚI: chỉ cần gọi dtPreset(...) với nhãn + số đo riêng — không cần helper mới.
const dtPreset = (
  key: string, label: string, bass: string, cents: number,
  scaleNotes: readonly PresetNote[], hint: string,
  opts: { lowestDegreeIdx?: number; octave?: number } = {},
): TuningPreset => {
  const size = scaleNotes.length;
  const lowestIdx = opts.lowestDegreeIdx ?? Math.max(0, size - LOWEST_BELOW_ROOT);
  const lowestCents = scaleNotes[lowestIdx]?.cents ?? 0;
  return p(key, label, size, scaleNotes, bass, opts.octave ?? 2, hint,
    { defaultLowestDegreeIdx: lowestIdx, defaultBassCents: cents + lowestCents });
};
// Hò (bậc 0) là dây thứ mấy (thấp→cao, 1-based) — cho nhãn hint.
const hoString = (size: number, lowestIdx: number) => ((size - lowestIdx) % size) + 1;
// VỌNG CỔ = dtPreset với nhãn "Vọng cổ — ". Chỉ dùng cho TABC, KHÔNG cho hát ru.
const vc = (
  key: string, name: string, bass: string, cents: number,
  scaleNotes: readonly PresetNote[] = VC_PENTA,
  opts: { lowestDegreeIdx?: number; octave?: number } = {},
): TuningPreset => {
  const lowestIdx = opts.lowestDegreeIdx ?? Math.max(0, scaleNotes.length - LOWEST_BELOW_ROOT);
  const hoPos = hoString(scaleNotes.length, lowestIdx);
  return dtPreset('vc-' + key, 'Vọng cổ — ' + name, bass, cents, scaleNotes,
    `Hò = ${bass}${(opts.octave ?? 2) + 1}${cents >= 0 ? '+' : ''}${cents}¢ (dây gốc ĐỎ, dây thứ ${hoPos} thấp→cao). `
    + `Quãng 5 (VÀNG) = Hò+700¢. ${scaleNotes === VC_PENTA ? 'Tạm khuôn C-D-E-G-A' : 'Đo từ overlay'} "${name}".`,
    opts);
};
// HÁT RU MIỀN NAM = dtPreset nhãn "Ru Con Miền Nam — " (THỂ LOẠI, song song vọng cổ).
//   KHÔNG có khuôn mặc định — luôn truyền số microtone đo từ overlay [DTT] của người hát.
const rc = (
  key: string, name: string, bass: string, cents: number,
  scaleNotes: readonly PresetNote[],
  opts: { lowestDegreeIdx?: number; octave?: number } = {},
): TuningPreset => {
  const lowestIdx = opts.lowestDegreeIdx ?? Math.max(0, scaleNotes.length - LOWEST_BELOW_ROOT);
  const hoPos = hoString(scaleNotes.length, lowestIdx);
  return dtPreset('rc-' + key, 'Ru Con Miền Nam — ' + name, bass, cents, scaleNotes,
    `Hò = ${bass}${(opts.octave ?? 2) + 1}${cents >= 0 ? '+' : ''}${cents}¢ (dây gốc ĐỎ, dây thứ ${hoPos} thấp→cao). `
    + `Quãng 5 (VÀNG) = Hò+700¢. Microtone đo từ overlay "${name}".`,
    opts);
};

export const TUNING_PRESETS: readonly TuningPreset[] = [
  // ---- 1 note: octave ladder ----
  p('octave-ladder', 'Octave Ladder (1)', 1, [n(0)], 'C', 2, 'Every string is +12 st (one octave).'),

  // ---- 2 notes ----
  p('power-fifths', 'Fifths (2)', 2, [n(0), n(7)], 'C', 2, 'Bass and its perfect 5th, stacked in octaves.'),
  p('power-fourths', 'Fourths (2)', 2, [n(0), n(5)], 'C', 2),

  // ---- 3 notes ----
  p('triad-major', 'Major Triad (3)', 3, [n(0), n(4), n(7)], 'C', 2),
  p('triad-minor', 'Minor Triad (3)', 3, [n(0), n(3), n(7)], 'C', 2),
  p('triad-sus4', 'Sus4 Triad (3)', 3, [n(0), n(5), n(7)], 'C', 2),

  // ---- 4 notes ----
  p('tetra-dim7', 'Diminished 7 (4)', 4, [n(0), n(3), n(6), n(9)], 'C', 2),
  p('tetra-7', 'Dominant 7 (4)', 4, [n(0), n(4), n(7), n(10)], 'C', 2),

  // ---- 5 notes: PENTATONIC family (đàn tranh home turf) ----
  p('pentatonic-major', 'Pentatonic Major (5)', 5, [n(0), n(2), n(4), n(7), n(9)], 'C', 2,
    undefined, { sourceUrl: 'https://en.wikipedia.org/wiki/Pentatonic_scale' }),
  p('vietnamese-nam', 'Vietnamese Nam (5)', 5, [n(0), n(3), n(5), n(7), n(10)], 'A', 2,
    'Melancholic southern mode (= pentatonic minor).',
    { sourceUrl: 'https://en.wikipedia.org/wiki/Music_of_Vietnam' }),
  p(
    'vietnamese-hue',
    'Vietnamese Huế (5)',
    5,
    // Approximate Huế "oán" tuning — 2nd and 5th degrees tuned about a quartertone
    // flat of equal temperament (this is a taste, not a standard).
    [n(0), n(2, -35), n(5), n(7), n(9, -30)],
    'C',
    2,
    'Central Vietnamese — 2nd/6th are lowered ~35¢ for the "oán" color.',
    { sourceUrl: 'https://en.wikipedia.org/wiki/Music_of_Vietnam' },
  ),
  p(
    'japanese-hirajoshi',
    'Japanese Hirajoshi 平調子 (5)',
    5,
    [n(0), n(2), n(3), n(7), n(8)],
    'A',
    2,
    'Classic koto tuning (A B C E F); used in "Sakura Sakura". Lowest = E3.',
    {
      // Pin string[0] to the 4th degree (E) of the In scale → bottom string E3.
      defaultLowestDegreeIdx: 3,
      sourceUrl: 'https://en.wikipedia.org/wiki/Japanese_musical_scales',
    },
  ),
  p(
    'slendro',
    'Slendro (5, ≈5-EDO)',
    5,
    // Equal 240¢ steps — generic Central Javanese approximation; real
    // gamelans deviate ±25¢ per step (Surjodiningrat 1972).
    [n(0), n(2, 40), n(4, 80), n(7, 20), n(9, 60)],
    'C',
    2,
    'Javanese gamelan, floating no-semitone feel. Approximate — real ensembles vary.',
    { sourceUrl: 'https://en.wikipedia.org/wiki/Slendro' },
  ),

  // ---- 5 notes: VỌNG CỔ "Tình Anh Bán Chiếu" — lên dây đàn tranh theo 7 danh ca ----
  // (xem vc() bên trên: cùng ngũ cung C-D-E-G-A, chỉ khác TÔNG GỐC Hò mỗi danh ca.)
  vc('ktl', 'Kim Tử Long', 'G#', 1, [n(0), n(2), n(6, 5), n(7), n(11, -17)]),   // overlay: Hò=G#3+1c
  vc('pq', 'Phương Quang', 'G#', -21, [n(0), n(2), n(4, -22), n(7), n(9)]),   // overlay: Hò=G#3-21c
  vc('mc', 'Minh Cảnh', 'A', -28, [n(0), n(2), n(4, -41), n(7), n(8, -9)]),   // overlay: Hò=A3-28c
  vc('tp', 'Trọng Phúc', 'A', 0, [n(0), n(2, -23), n(5, 33), n(7), n(8, -13)]),   // overlay: Hò=A3+0c
  vc('uto', 'Út Trà Ôn', 'G#', 1, [n(0), n(4, 24), n(6, -39), n(7), n(8, -36)]),   // overlay: Hò=G#3+1c
  vc('tn', 'Trần Nên', 'G#', 8, [n(0), n(4, -27), n(6, -20), n(7), n(8, 43)]),   // overlay: Hò=G#3+8c
  vc('hvt', 'Hoàng Việt Trang', 'G#', 3, [n(0), n(4, -39), n(5, 31), n(6, 7), n(7)]),   // overlay: Hò=G#3+3c

  // ---- RU CON MIỀN NAM (thể loại riêng, KHÔNG phải vọng cổ) — lên dây MICROTONE đo từ overlay ----
  // Bố trí 'D E G A C': 2 dây (D, E) DƯỚI dây G (quãng-5) → lowestDegreeIdx: 1 (trỏ bậc D).
  rc('bichtuyen', 'Bích Tuyền', 'C', 10, [n(0), n(2), n(4, -22), n(7), n(9, -28)],
    { lowestDegreeIdx: 1, octave: 3 }),   // octave 3; [DTT] đo từ overlay: Hò=C+10¢, E−22¢, A−28¢ (microtone)

  // ---- 6 notes ----
  p('whole-tone', 'Whole Tone (6)', 6, [n(0), n(2), n(4), n(6), n(8), n(10)], 'C', 2),
  p('blues', 'Blues (6)', 6, [n(0), n(3), n(5), n(6), n(7), n(10)], 'A', 2),

  // ---- 7 notes: heptatonic family ----
  p('heptatonic-major', 'Major (7)', 7, [n(0), n(2), n(4), n(5), n(7), n(9), n(11)], 'C', 2,
    undefined, { sourceUrl: 'https://en.wikipedia.org/wiki/Major_scale' }),
  p('heptatonic-minor', 'Natural Minor (7)', 7, [n(0), n(2), n(3), n(5), n(7), n(8), n(10)], 'A', 2,
    undefined, { sourceUrl: 'https://en.wikipedia.org/wiki/Minor_scale' }),
  p('dorian', 'Dorian (7)', 7, [n(0), n(2), n(3), n(5), n(7), n(9), n(10)], 'D', 2,
    undefined, { sourceUrl: 'https://en.wikipedia.org/wiki/Dorian_mode' }),
  p('phrygian', 'Phrygian (7)', 7, [n(0), n(1), n(3), n(5), n(7), n(8), n(10)], 'E', 2,
    undefined, { sourceUrl: 'https://en.wikipedia.org/wiki/Phrygian_mode' }),
  p('lydian', 'Lydian (7)', 7, [n(0), n(2), n(4), n(6), n(7), n(9), n(11)], 'F', 2,
    undefined, { sourceUrl: 'https://en.wikipedia.org/wiki/Lydian_mode' }),
  p('mixolydian', 'Mixolydian (7)', 7, [n(0), n(2), n(4), n(5), n(7), n(9), n(10)], 'G', 2,
    undefined, { sourceUrl: 'https://en.wikipedia.org/wiki/Mixolydian_mode' }),
  p('harmonic-minor', 'Harmonic Minor (7)', 7, [n(0), n(2), n(3), n(5), n(7), n(8), n(11)], 'A', 2,
    undefined, { sourceUrl: 'https://en.wikipedia.org/wiki/Minor_scale#Harmonic_minor_scale' }),

  // ---- 7 notes: JUST / PYTHAGOREAN heptatonic ----
  p(
    'just-major',
    'Just Major 5-limit (7)',
    7,
    // 1/1, 9/8, 5/4, 4/3, 3/2, 5/3, 15/8 — Ptolemy intense diatonic.
    [n(0), n(2, 4), n(3, 86), n(4, 98), n(7, 2), n(8, 84), n(10, 88)],
    'C', 2,
    'Pure 5-limit ratios — beatless triads, the "honest" major.',
    { sourceUrl: 'https://en.wikipedia.org/wiki/Five-limit_tuning' },
  ),
  p(
    'just-minor',
    'Just Natural Minor 5-limit (7)',
    7,
    // 1/1, 9/8, 6/5, 4/3, 3/2, 8/5, 9/5.
    [n(0), n(2, 4), n(3, 16), n(4, 98), n(7, 2), n(8, 14), n(10, 18)],
    'A', 2,
    'Pure minor third (6/5) and sixth (8/5) — warm, dark.',
    { sourceUrl: 'https://en.wikipedia.org/wiki/Five-limit_tuning' },
  ),
  p(
    'pythagorean',
    'Pythagorean Diatonic (7)',
    7,
    // Stack of pure 3/2 fifths reduced into an octave.
    [n(0), n(2, 3.91), n(4, 7.82), n(4, 98.04), n(7, 1.96), n(9, 5.87), n(11, 9.78)],
    'C', 2,
    'Stack of pure 3/2 fifths — bright wide thirds, medieval feel.',
    { sourceUrl: 'https://en.wikipedia.org/wiki/Pythagorean_tuning' },
  ),

  // ---- 7 notes: MAQAM (24-EDO theoretical model — practice varies) ----
  p(
    'maqam-rast',
    'Maqam Rast (7)',
    7,
    // Signature neutral third (~355¢) and neutral seventh (~1057¢).
    [n(0), n(2, 4), n(3, 55), n(4, 98), n(7, 2), n(9, 6), n(10, 57)],
    'C', 2,
    'Arabic maqam — neutral 3rd/7th. Cents are theoretical; practice varies regionally.',
    { sourceUrl: 'https://en.wikipedia.org/wiki/Arabic_maqam' },
  ),
  p(
    'maqam-bayati',
    'Maqam Bayati (7)',
    7,
    // Neutral second (~150¢) on the Bayati tetrachord.
    [n(0), n(1, 50), n(3, 2), n(4, 98), n(7, 2), n(8, 14), n(10, 4)],
    'D', 2,
    'Arabic maqam with neutral 2nd — the introspective "weeping" mode. 24-EDO model.',
    { sourceUrl: 'https://en.wikipedia.org/wiki/Arabic_maqam' },
  ),

  // ---- 8 notes ----
  p(
    'bebop-major',
    'Bebop Major (8)',
    8,
    [n(0), n(2), n(4), n(5), n(7), n(8), n(9), n(11)],
    'C',
    2,
  ),
  p(
    'diminished',
    'Octatonic / Diminished (8)',
    8,
    [n(0), n(2), n(3), n(5), n(6), n(8), n(9), n(11)],
    'C',
    2,
  ),

  // ---- 12 notes: equal & historical temperaments ----
  p(
    'chromatic',
    'Chromatic 12-EDO (12)',
    12,
    Array.from({ length: 12 }, (_, i) => n(i)),
    'C',
    2,
    undefined,
    { sourceUrl: 'https://en.wikipedia.org/wiki/12_equal_temperament' },
  ),
  p(
    'meantone-quarter-comma',
    '¼-Comma Meantone (12)',
    12,
    // Renaissance/early-Baroque: pure 5/4 major thirds; wolf G#-Eb.
    [
      n(0), n(0, 76.05), n(1, 93.16), n(3, 10.26), n(3, 86.31), n(5, 3.42),
      n(5, 79.47), n(6, 96.58), n(7, 72.63), n(8, 89.74), n(10, 6.84), n(10, 82.89),
    ],
    'C', 2,
    'Pure 5/4 thirds; wolf 5th at G♯–E♭. Bach-era keyboard tuning.',
    { sourceUrl: 'https://en.wikipedia.org/wiki/Quarter-comma_meantone' },
  ),
  p(
    'werckmeister-iii',
    'Werckmeister III (12)',
    12,
    // 1691 "well temperament": every key playable, each has its own color.
    [
      n(0), n(0, 90.225), n(1, 92.180), n(2, 94.135), n(3, 90.225), n(4, 98.045),
      n(5, 88.270), n(6, 96.090), n(7, 92.180), n(8, 88.270), n(9, 96.090), n(10, 92.180),
    ],
    'C', 2,
    'Bach-era well temperament — every key works, but each has its own color.',
    { sourceUrl: 'https://en.wikipedia.org/wiki/Werckmeister_temperament' },
  ),
  p(
    'kirnberger-iii',
    'Kirnberger III (12)',
    12,
    // 1779: pure C-E third, syntonic comma absorbed in the D-A fifth.
    [
      n(0), n(0, 90.225), n(1, 93.157), n(2, 94.135), n(3, 86.314), n(4, 98.045),
      n(5, 90.224), n(6, 96.578), n(7, 92.180), n(8, 89.735), n(9, 96.090), n(10, 88.269),
    ],
    'C', 2,
    'Bach-era well temperament with a pure C–E third.',
    { sourceUrl: 'https://en.wikipedia.org/wiki/Kirnberger_temperament' },
  ),

  // ---- EDO variants (equal division of octave) ----
  p(
    'edo7',
    '7-EDO (7)',
    7,
    Array.from({ length: 7 }, (_, i) => {
      const totalCents = (i * 1200) / 7;
      const semi = Math.floor(totalCents / 100);
      const cents = totalCents - semi * 100;
      return n(semi, cents);
    }),
    'C',
    2,
    'Equal division of octave into 7 steps — each ≈ 171¢.',
    { sourceUrl: 'https://en.xen.wiki/w/7edo' },
  ),
  p(
    'edo17',
    '17-EDO (17)',
    17,
    Array.from({ length: 17 }, (_, i) => n(0, (i * 1200) / 17)),
    'C',
    2,
    'Equal division of octave into 17 steps — microtonal.',
    { sourceUrl: 'https://en.xen.wiki/w/17edo' },
  ),

  // ---- Non-octave xenharmonic ----
  p(
    'bohlen-pierce-lambda',
    'Bohlen-Pierce Lambda (9, tritave)',
    9,
    // Period is the tritave 3/1 (1902¢), not the octave. Nine Just-intoned
    // pitches: 1/1, 25/21, 9/7, 7/5, 5/3, 9/5, 15/7, 7/3, 25/9.
    [
      n(0), n(3, 1.8), n(4, 35.1), n(5, 82.5), n(8, 84.4),
      n(10, 17.6), n(13, 19.4), n(14, 66.9), n(17, 68.7),
    ],
    'C', 2,
    'No octaves — repeats every tritave (3/1). Alien but consonant.',
    {
      scalePeriod: 1902,
      sourceUrl: 'https://www.huygens-fokker.org/bpsite/modes.html',
    },
  ),
  p(
    'carlos-alpha',
    'Carlos Alpha (1, 78¢ generator)',
    1,
    // Wendy Carlos's non-octave generator — stack of pure 78¢ steps.
    // 9 alphas ≈ 3/2 fifth (702¢); 5 alphas ≈ 5/4 third (390¢).
    [n(0)],
    'C', 2,
    '78¢ generator, no octave — Wendy Carlos. 9 steps ≈ pure 5th.',
    {
      scalePeriod: 78,
      sourceUrl: 'https://en.wikipedia.org/wiki/Alpha_scale',
    },
  ),
] as const;

export function getPresetByKey(key: string): TuningPreset | undefined {
  return TUNING_PRESETS.find((p) => p.key === key);
}

export function LIST_PRESETS(): readonly TuningPreset[] {
  return TUNING_PRESETS;
}
